/**
 * Multiplexed WebSocket singleton client
 *
 * Provides a single shared WebSocket connection to /ws with:
 *   - Lazy connect on first subscribe
 *   - Topic-based subscribe/unsubscribe with refcounting
 *   - Auto-reconnect with exponential backoff (mirrors useTickStream schedule)
 *   - Buffered subscribe messages flushed after 'ready'
 *   - Re-subscribes all active topics after reconnect
 *   - 25s client-side heartbeat ping
 */

import { useAuthStore } from '../../store/authStore.js';
import { logger } from '../logger.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TopicHandler = (data: unknown) => void;

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

// ---------------------------------------------------------------------------
// Constants — mirror useTickStream for consistency
// ---------------------------------------------------------------------------

const HEARTBEAT_INTERVAL_MS = 25_000;
const PONG_TIMEOUT_MS = 5_000;
const MAX_MISSED_PONGS = 2;
const RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

// ---------------------------------------------------------------------------
// WsClient class
// ---------------------------------------------------------------------------

export class WsClient {
  private ws: WebSocket | null = null;
  private status: ConnectionStatus = 'disconnected';

  /** topic → Set<handler> */
  private handlers = new Map<string, Set<TopicHandler>>();

  /** Topics buffered while disconnected — flushed after 'ready' */
  private pendingSubscribes = new Set<string>();
  private pendingUnsubscribes = new Set<string>();

  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private missedPongs = 0;

  private isAuthenticated = false;

  /** True once the FIRST `ready` has been received — used to distinguish reconnect from initial auth. */
  private hadPriorAuth = false;

  /** Listeners notified whenever a `ready` arrives AFTER the first auth (reconnect). */
  private reconnectListeners = new Set<() => void>();

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Register a callback fired whenever the singleton authenticates after a prior
   * session — i.e. after a reconnect. Use it to refetch authoritative state and
   * patch the disconnection gap (Redis pub/sub does not replay missed events).
   * Returns an unregister function.
   */
  onReconnect(listener: () => void): () => void {
    this.reconnectListeners.add(listener);
    return () => {
      this.reconnectListeners.delete(listener);
    };
  }

  /**
   * Subscribe to a topic. Returns a cleanup function that unsubscribes.
   * Multiple callers for the same topic each register their own handler;
   * the underlying subscription is maintained until the last caller unsubscribes.
   */
  subscribe(topic: string, handler: TopicHandler): () => void {
    if (!this.handlers.has(topic)) {
      this.handlers.set(topic, new Set());
    }
    this.handlers.get(topic)!.add(handler);

    // If this is the first subscriber for this topic, tell the server
    if (this.handlers.get(topic)!.size === 1) {
      this.sendSubscribe([topic]);
    }

    // Lazy connect on first subscription
    if (this.status === 'disconnected') {
      this.connect();
    }

    return () => {
      this.unsubscribe(topic, handler);
    };
  }

  isConnected(): boolean {
    return this.status === 'connected' && this.isAuthenticated;
  }

  connect(): void {
    if (this.status !== 'disconnected') return;
    this.doConnect();
  }

  disconnect(): void {
    this.clearReconnectTimer();
    this.clearHeartbeat();
    this.status = 'disconnected';
    this.isAuthenticated = false;

    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.onopen = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      try {
        ws.close();
      } catch {
        // ignore
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private unsubscribe(topic: string, handler: TopicHandler): void {
    const set = this.handlers.get(topic);
    if (!set) return;
    set.delete(handler);

    if (set.size === 0) {
      this.handlers.delete(topic);
      this.sendUnsubscribe([topic]);
    }
  }

  private sendSubscribe(topics: string[]): void {
    if (!this.isAuthenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Buffer for later
      for (const t of topics) {
        this.pendingSubscribes.add(t);
        this.pendingUnsubscribes.delete(t);
      }
      return;
    }
    try {
      this.ws.send(JSON.stringify({ type: 'subscribe', topics }));
    } catch (err) {
      logger.error('[WsClient] Failed to send subscribe:', err);
    }
  }

  private sendUnsubscribe(topics: string[]): void {
    if (!this.isAuthenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Buffer for later
      for (const t of topics) {
        this.pendingUnsubscribes.add(t);
        this.pendingSubscribes.delete(t);
      }
      return;
    }
    try {
      this.ws.send(JSON.stringify({ type: 'unsubscribe', topics }));
    } catch (err) {
      logger.error('[WsClient] Failed to send unsubscribe:', err);
    }
  }

  private flushPending(): void {
    if (this.pendingUnsubscribes.size > 0) {
      const topics = [...this.pendingUnsubscribes];
      this.pendingUnsubscribes.clear();
      this.sendUnsubscribe(topics);
    }
    if (this.pendingSubscribes.size > 0) {
      const topics = [...this.pendingSubscribes];
      this.pendingSubscribes.clear();
      this.sendSubscribe(topics);
    }
  }

  private resubscribeAll(): void {
    const topics = [...this.handlers.keys()];
    if (topics.length === 0) return;
    try {
      this.ws!.send(JSON.stringify({ type: 'subscribe', topics }));
    } catch (err) {
      logger.error('[WsClient] Failed to resubscribe:', err);
    }
  }

  private doConnect(): void {
    this.status = 'connecting';

    const envWsUrl = (import.meta as { env?: { VITE_WS_URL?: string } }).env?.VITE_WS_URL;
    const wsUrl = envWsUrl
      ? `${envWsUrl}`
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;

    logger.info('[WsClient] Connecting to', wsUrl);
    const ws = new WebSocket(wsUrl);
    this.ws = ws;

    ws.onopen = () => {
      logger.info('[WsClient] Connected');
      this.reconnectAttempt = 0;

      const token = useAuthStore.getState().tokens?.accessToken ?? null;
      if (token) {
        ws.send(JSON.stringify({ type: 'auth', token }));
      } else {
        logger.warn('[WsClient] No auth token — cannot authenticate');
      }

      this.startHeartbeat();
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as Record<string, unknown>;
        this.handleServerMessage(msg);
      } catch (err) {
        logger.error('[WsClient] Failed to parse message:', err);
      }
    };

    ws.onclose = () => {
      logger.info('[WsClient] Disconnected');
      this.clearHeartbeat();
      this.isAuthenticated = false;
      this.ws = null;
      this.status = 'disconnected';
      this.scheduleReconnect();
    };

    ws.onerror = (err) => {
      logger.error('[WsClient] WebSocket error:', err);
      // onclose fires after onerror
    };
  }

  private handleServerMessage(msg: Record<string, unknown>): void {
    switch (msg.type) {
      case 'ready': {
        this.status = 'connected';
        this.isAuthenticated = true;
        logger.info('[WsClient] Authenticated, userId:', msg.userId);
        // Re-subscribe all active topics (covers reconnect case)
        this.resubscribeAll();
        // Then flush any buffered subscribe/unsubscribe from while we were offline
        this.flushPending();
        // Notify reconnect listeners so callers can refetch authoritative state.
        if (this.hadPriorAuth) {
          for (const listener of this.reconnectListeners) {
            try {
              listener();
            } catch (err) {
              logger.error('[WsClient] Reconnect listener error:', err);
            }
          }
        }
        this.hadPriorAuth = true;
        break;
      }

      case 'event': {
        const topic = msg.topic as string | undefined;
        const data = msg.data;
        if (!topic) return;
        const set = this.handlers.get(topic);
        if (!set) return;
        for (const handler of set) {
          try {
            handler(data);
          } catch (err) {
            logger.error('[WsClient] Handler error for topic', topic, err);
          }
        }
        break;
      }

      case 'pong': {
        // Clear watchdog
        if (this.pongTimer) {
          clearTimeout(this.pongTimer);
          this.pongTimer = null;
        }
        this.missedPongs = 0;
        break;
      }

      case 'auth-error': {
        logger.error('[WsClient] Auth error:', msg.reason);
        break;
      }

      case 'error': {
        logger.error('[WsClient] Server error:', msg.message);
        break;
      }

      default:
        break;
    }
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();

    this.heartbeatTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

      try {
        this.ws.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
      } catch (err) {
        logger.error('[WsClient] Failed to send ping:', err);
        this.handleConnectionDeath('ping send failure');
        return;
      }

      if (this.pongTimer) clearTimeout(this.pongTimer);
      this.pongTimer = setTimeout(() => {
        this.pongTimer = null;
        this.missedPongs += 1;
        logger.warn(`[WsClient] Missed pong (${this.missedPongs}/${MAX_MISSED_PONGS})`);
        if (this.missedPongs >= MAX_MISSED_PONGS) {
          this.handleConnectionDeath('heartbeat timeout');
        }
      }, PONG_TIMEOUT_MS);
    }, HEARTBEAT_INTERVAL_MS);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
    this.missedPongs = 0;
  }

  private handleConnectionDeath(reason: string): void {
    logger.warn('[WsClient] Connection dead:', reason);
    this.clearHeartbeat();

    const ws = this.ws;
    this.ws = null;
    this.isAuthenticated = false;
    this.status = 'disconnected';

    if (ws) {
      ws.onopen = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      try {
        ws.close();
      } catch {
        // ignore
      }
    }

    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return; // already scheduled
    if (this.handlers.size === 0) return; // no active subscribers — don't reconnect

    const delay =
      RECONNECT_DELAYS_MS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)];
    this.reconnectAttempt += 1;

    logger.info(`[WsClient] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.status === 'disconnected') {
        this.doConnect();
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const wsClient = new WsClient();
